import { useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

export const MAX_GALLERY_PHOTOS = 4;

function previewSrc(url) {
  return url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

export function ChronicleGalleryField({ form, update }) {
  const photos = form.photos || [];
  const [uploading, setUploading] = useState(false);
  const [albums, setAlbums] = useState(null);
  const [link, setLink] = useState("");
  const remaining = MAX_GALLERY_PHOTOS - photos.length;
  const setPhotos = (next) => update("photos", next.slice(0, MAX_GALLERY_PHOTOS));
  const add = (url) => {
    if (!url || remaining <= 0) return toast.info(`Máximo ${MAX_GALLERY_PHOTOS} fotografías`);
    setPhotos([...photos, { url, caption: "" }]);
  };
  const upload = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, remaining);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/admin/chronicles/photo", body, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: data.url, caption: "" });
      }
      setPhotos([...photos, ...uploaded]);
      toast.success(`${uploaded.length} fotografía${uploaded.length > 1 ? "s" : ""} subida${uploaded.length > 1 ? "s" : ""}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };
  const searchFlickr = async () => {
    try {
      const { data } = await api.get("/admin/flickr/match", { params: { date: form.date, location: form.location } });
      setAlbums(data);
      if (!data.length) toast.info("No hay álbumes de Flickr con esa fecha y localidad");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div className="grid gap-3 border border-white/10 bg-white/[0.02] p-4" data-testid="admin-chronicle-gallery-field">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="field-label">Galería bajo la entradilla (hasta {MAX_GALLERY_PHOTOS} fotos con pie)</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="admin-chronicle-gallery-count">{photos.length}/{MAX_GALLERY_PHOTOS}</span>
      </div>
      {photos.length ? (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="admin-chronicle-gallery-list">
          {photos.map((photo, index) => (
            <div key={`${photo.url}-${index}`} className="grid gap-2 border border-white/10 bg-charcoal p-2" data-testid={`admin-chronicle-gallery-item-${index}`}>
              <img src={previewSrc(photo.url)} alt="" className="h-28 w-full object-cover" data-testid={`admin-chronicle-gallery-preview-${index}`} />
              <Input value={photo.caption} onChange={(event) => setPhotos(photos.map((item, i) => (i === index ? { ...item, caption: event.target.value } : item)))} placeholder="Pie de foto" className="h-9 rounded-none border-white/10 bg-panel text-xs text-slate-100 placeholder:text-slate-600" data-testid={`admin-chronicle-gallery-caption-${index}`} />
              <button type="button" onClick={() => setPhotos(photos.filter((_, i) => i !== index))} className="inline-flex items-center gap-1 justify-self-end font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-red-300" data-testid={`admin-chronicle-gallery-remove-${index}`}><Trash2 className="h-3 w-3" /> Quitar</button>
            </div>
          ))}
        </div>
      ) : null}
      {remaining > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 border border-clay/40 bg-clay/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal">
              <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="admin-chronicle-gallery-upload" />
              {uploading ? "Subiendo..." : "Subir fotos"} <ImagePlus className="h-3 w-3" />
            </label>
            <Button type="button" variant="outline" onClick={searchFlickr} disabled={!form.date} className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.2em]" data-testid="admin-chronicle-gallery-flickr-search">Buscar en Flickr por fecha</Button>
          </div>
          {albums?.length ? (
            <div className="flex flex-wrap gap-2" data-testid="admin-chronicle-gallery-flickr-covers">
              {albums.map((album) => <button key={album.album_id || album.url} type="button" onClick={() => add(album.cover_url)} className="border border-white/10 hover:border-clay" title={album.label}><img src={album.cover_url} alt={album.label} className="h-16 w-24 object-cover" /><span className="block px-1 font-mono text-[8px] uppercase text-slate-400">{album.label}</span></button>)}
            </div>
          ) : null}
          <div className="flex gap-2">
            <Input value={link} onChange={(event) => setLink(event.target.value)} placeholder="O pega un enlace de foto (Flickr o imagen de Internet)" className="h-10 rounded-none border-white/10 bg-charcoal text-xs text-slate-100 placeholder:text-slate-600" data-testid="admin-chronicle-gallery-link-input" />
            <Button type="button" variant="outline" disabled={!link.startsWith("http")} onClick={() => { add(link.trim()); setLink(""); }} className="h-10 rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.2em]" data-testid="admin-chronicle-gallery-link-add">Añadir</Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
